import Papa from "papaparse";
import { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ButtonGroup from "@mui/material/ButtonGroup";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { styled } from "@mui/material/styles";
import {
  actSubjects,
  subjectQuestionCount,
} from "../../actprep-ui/constants/program";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { capitalize } from "../../actprep-ui/utils/formatting";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const numQuestionsBySubject = {
  english: 75,
  math: 60,
  reading: 40,
  science: 40,
};

export default function ConversionChartEditor({ test, onSubmit }) {
  const [subject, setSubject] = useState("english");
  const [conversionChart, setConversionChart] = useState({});
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!test || !test.conversion_chart) return;

    const initialChart = test.conversion_chart[subject] || {};
    setConversionChart(initialChart);
  }, [test, subject]);

  useEffect(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [subject]);

  const handleScoreChange = (rawScore, newACTScore) => {
    setConversionChart((prevChart) => ({
      ...prevChart,
      [rawScore]: newACTScore,
    }));
  };

  const numQuestions = numQuestionsBySubject[subject];
  const rows = Array.from({ length: numQuestions }, (_, idx) => {
    const rawScore = idx + 1;
    const actScore = conversionChart[rawScore] || "";
    return {
      id: rawScore,
      rawScore,
      actScore,
    };
  });

  const columns = [
    { field: "rawScore", headerName: "Raw Score", width: 150 },
    {
      field: "actScore",
      headerName: "ACT Score",
      width: 150,
      renderCell: (params) => (
        <TextField
          variant="standard"
          value={params.value}
          onChange={(event) => handleScoreChange(params.id, event.target.value)}
        />
      ),
    },
  ];

  const processConversionChartForSubject = (data, selectedSubject) => {
    const conversionData = {};
    const formattedSubject = capitalize(selectedSubject);
    const maxQuestions = subjectQuestionCount[test.variant][subject];
    const sortedData = data
      .filter(
        (row) => row[formattedSubject]?.trim() && row["Scale Score"]?.trim()
      )
      .map((row) => ({
        rawScore: parseInt(row[formattedSubject].trim(), 10),
        actScore: parseInt(row["Scale Score"].trim(), 10),
      }))
      .sort((a, b) => a.rawScore - b.rawScore);

    let previousActScore = sortedData[0].actScore;

    for (let i = 0; i < sortedData.length; i++) {
      const { rawScore, actScore } = sortedData[i];

      if (i > 0) {
        const prevRawScore = sortedData[i - 1].rawScore;
        for (
          let gapRawScore = prevRawScore + 1;
          gapRawScore < rawScore;
          gapRawScore++
        ) {
          conversionData[gapRawScore] = previousActScore;
        }
      }

      conversionData[rawScore] = actScore;
      previousActScore = actScore;
    }

    const firstRawScore = sortedData[0].rawScore;
    if (firstRawScore > 0) {
      conversionData[0] = previousActScore;
    }

    const lastRawScore = sortedData[sortedData.length - 1].rawScore;
    for (
      let remainingRawScore = lastRawScore + 1;
      remainingRawScore <= maxQuestions;
      remainingRawScore++
    ) {
      conversionData[remainingRawScore] = previousActScore;
    }

    return conversionData;
  };

  const handleCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const results = await new Promise((resolve) =>
      Papa.parse(file, {
        header: true,
        complete: resolve,
      })
    );
    const processedData = processConversionChartForSubject(
      results.data,
      subject
    );
    setConversionChart(processedData);
    showSuccessSnackbar("Conversion chart uploaded!");
    event.target.value = "";
  };

  const handleSubmit = () => {
    onSubmit(subject, conversionChart);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormControl fullWidth margin="normal" variant="standard">
        <InputLabel>Subject</InputLabel>
        <Select
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          label="Subject"
        >
          {actSubjects.map((subject) => (
            <MenuItem key={subject} value={subject}>
              {capitalize(subject)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <ButtonGroup variant="outlined" fullWidth>
        <Button fullWidth component="label" startIcon={<CloudUploadIcon />}>
          Upload Chart
          <VisuallyHiddenInput
            type="file"
            accept=".csv"
            onChange={handleCSVUpload}
            ref={fileInputRef}
          />
        </Button>
        <Button color="primary" onClick={handleSubmit}>
          Save
        </Button>
      </ButtonGroup>
      <Box sx={{ height: 400, width: "100%" }}>
        <DataGridPro rows={rows} columns={columns} />
      </Box>
    </Box>
  );
}
